
import formMixin from './form-mixin'
import eventLogging from '~/components/embedded/event-logging-mixin.js'
import { mapState } from 'pinia'
import { useProgressStore } from '~/store/progress'
import { useQuestionsStore } from '~/store/questions'

import { useVuelidate } from '@vuelidate/core'

/**
 * Mixin to adjust a page to have standard fields and methods required for an application-form page
 */
export default {
  data() {
    return {
      /**
       * Prevents form interactions when transitioning between pages
       */
      transitioning: false,

      /**
       * App Name for a page enables it to do the correct navigation forward and back
       */
      appName: null,

      /**
       * Auto submit some fields on entry
       */
      autoSubmit: false,
    }
  },
  mixins: [
    formMixin,
    eventLogging
  ],
  computed: {
    ...mapState(useProgressStore, ['navigating']),
    ...mapState(useQuestionsStore, ['loadingQuestions', 'savingAnswers']),
    reversing() {
      return this.navigating == 'back'
    },
    advancing() {
      return this.navigating == 'forward'
    },
    editing() {
      return this.$route.query.editing
    },
    editingParam() {
      return this.editing ? '?editing=true' : ''
    },
  },

  async mounted() {
    this.mountedOverride()
  },

  setup() {
    return { v$: useVuelidate() }
  },

  methods: {
    /**
     * Navigate to a previous page in the current application
     */
    previousPage () {
      useProgressStore(this.$pinia).incrementRoute({
        currentPath: this.$route.path,
        appName: this.appName || undefined,
        forward: false
      })
    },

    mountedOverride() {
      this.setTabIndex()
    },

    /**
     * Ensure that the inputs have a tab index that allows for accessible filling out
     * without diving into other locations of the web page to start
     *
     * Tab indexes are relative, and setting 0 makes sure that all of these elements
     * end up being target-able and the dom will still inform order.
     */
    setTabIndex () {
      var inputs = document.getElementsByTagName('input')
      for (var x = 0; x < inputs.length; x++) {
        const el = document.getElementById(inputs[x].id)
        if (el) {
          el.tabIndex = 0
        }
      }
    },

    /**
     * Submit the answers for a form and navigate to the next route in the app.
     */
    async saveQuestions (autoSubmit = false, skipRouting = false, routeOverride = '') {
      // // Need this to keep QuestionNav disabled until next question appears.
      if (this.transitioning && !autoSubmit || this.savingAnswers) return

      this.transitioning = true
      this.reValidate()
      if (this.isInvalid) {
        this.transitioning = false
        return false
      }
      if (this.unsavedAnswers.peoPhone) {
        this.unsavedAnswers.peoPhone = this.unsavedAnswers.peoPhone.replace(/\D/g, '')
      }

      const success = await useQuestionsStore(this.$pinia).saveAnswers({ answers: this.unsavedAnswers})
      if (!success) {
        this.transitioning = false
        return false
      }

      // // Log Event to Embedded Service
      this.logUserEvent({
        action: this.$route.name + 'PageSubmit',
        data: this.formAnswers
      })
      this.clearAnswers()
      if (skipRouting) {
        return true
      } else {
        if (routeOverride) {
          useProgressStore(this.$pinia).appNavigate({
            route: routeOverride,
            forward: true
          })
        } else {
          useProgressStore(this.$pinia).incrementRoute({
            currentPath: this.$route.path,
            forward: true
          })
        }
      }
    },

    /**
     * Updates an answer if valid and triggers transitioning to be tre
     * @param {*} answer
     */
    updateQuestion ({ attr, isInvalid, submitImmediately }) {
      // Need this to keep QuestionNav disabled until next question appears.
      this.transitioning = true
      if (!attr || attr.alias === undefined || isInvalid) {
        this.transitioning = false
        return false
      }
      this.updateAnswer(...arguments)
      if (submitImmediately && this.autoSubmit) {
        this.saveQuestions(this.autoSubmit)
      } else {
        this.transitioning = false
      }
    },

    /**
     * Triggers vuelidate validation for the children components, which should prevent an invalid
     * form from going unreported to this component
     */
    reValidate ($component = null) {
      if (this.v$) {
        this.v$.$touch()
      }
    },
  }
}
